import {useDrawer} from "@/contexts/DrawerContext";
import {Button} from "@/components/ui/Button";
import {Input} from "@/components/ui/input";
import {SearchIcon, X} from "lucide-react";
import {FlowNodeData, FlowNodeTypes} from "@/types/node";
import {FlowNodesData} from "@/pages/flow/NodeTypes/NodesData";
import {useEffect, useState} from "react";
import {Node, useReactFlow} from "@xyflow/react";
import {useSelectedElements} from "@/hooks/useSelectedElements";
import {getSettingValueByKey} from "@/utils/index";
import {DRAWER_IDS} from "@/components/Drawers";
import {cn} from "@/lib/utils";

// offsets for fitting node in the middle of available viewport when drawers are open from left and right
const FIT_VIEW_OFFSET = 490;

type SearchResult = {
  id: string;
  nodeType: FlowNodeTypes;
  title: string;
  path: string[];
};

const SearchResultItem = ({
  nodeType,
  title,
  path,
  onClick,
}: {
  nodeType: FlowNodeTypes;
  title: string;
  path: string[];
  onClick: () => void;
}) => {
  const nodeTypeData = FlowNodesData[nodeType];

  return (
    <div
      className="flex items-start gap-2 px-2 py-1.5 hover:bg-accent rounded-md cursor-pointer transition-colors duration-200"
      onClick={onClick}
    >
      {nodeTypeData.icon && (
        <nodeTypeData.icon
          className={cn(
            "size-4 text-primary-accent flex-shrink-0",
            title.length > 0 ? "mt-1" : "mt-0.5",
          )}
        />
      )}
      <div className="flex flex-col max-w-full w-0 flex-grow pr-2">
        <p className="text-sm font-medium line-clamp-1 text-ellipsis overflow-hidden whitespace-nowrap">
          {title}
        </p>
        <p className="text-sm text-secondary line-clamp-1 text-ellipsis overflow-hidden whitespace-nowrap capitalize">
          {path.join(" • ")}
        </p>
      </div>
    </div>
  );
};

const Search = () => {
  const DRAWER_ID = DRAWER_IDS.SEARCH;
  const {Drawer, isDrawerOpen, closeDrawer} = useDrawer();
  const [search, setSearch] = useState("");
  // Search results only include node's string values and type
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const {setViewport, getNode, getNodes} = useReactFlow();
  const {setSelectedNode} = useSelectedElements();

  useEffect(() => {
    const nodes = getNodes() as Node<FlowNodeData>[];

    const results = nodes
      .map(node => {
        const nodeType = node.type;

        if (nodeType === FlowNodeTypes.PLACEHOLDER || node.data.isEmpty) {
          return null;
        }

        const searchLC = search.toLowerCase();
        const nodeTitle =
          getSettingValueByKey(node.data.settings, "name") ||
          getSettingValueByKey(node.data.settings, "jump_to_step") ||
          getSettingValueByKey(node.data.settings, "instructions") ||
          node.data.nodeTitle;
        const matchedText = [
          ...(nodeType.toLowerCase().includes(searchLC) ||
          node.id.toLowerCase().includes(searchLC)
            ? [{value: nodeTitle, settingLabel: node.data.settingsTitle}]
            : []),
          ...node.data.settings.flatMap(setting =>
            setting.params
              .map(p =>
                typeof p.value === "string" &&
                p.value.toLowerCase().includes(searchLC)
                  ? {value: p.value, settingLabel: setting.title}
                  : null,
              )
              .filter(
                (v): v is {value: string; settingLabel: string} => v !== null,
              ),
          ),
        ];

        if (matchedText.length > 0) {
          return {
            id: node.id,
            nodeType: nodeType as FlowNodeTypes,
            title: matchedText[0]?.value || "",
            path: [nodeType, matchedText[0]?.settingLabel || ""],
          };
        }
        return null;
      })
      .filter((result): result is SearchResult => result !== null);

    setSearchResults(results);
  }, [search, getNodes]);

  const fitToNode = (node: Node<FlowNodeData>) => {
    if (node) {
      setSelectedNode(node.id);

      const iframeDoc = window.__flowBuilderIframeDoc;
      const {x, y} = node.position;
      const {width, height} = node.measured;
      const zoom = 1.5;

      setViewport(
        {
          x:
            -x * zoom +
            iframeDoc.body.offsetWidth / 2 +
            width / 2 -
            FIT_VIEW_OFFSET,
          y: -y * zoom + iframeDoc.body.offsetHeight / 2 - height,
          zoom: zoom,
        },
        {duration: 1000},
      );
    }
  };

  return (
    isDrawerOpen(DRAWER_ID) && (
      <Drawer id={DRAWER_ID} position="left" width="w-75">
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center gap-1 px-5 py-5 pb-4">
            <h2 className="text-base font-semibold text-ellipsis overflow-hidden whitespace-nowrap">
              Search
            </h2>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => closeDrawer(DRAWER_ID)}
              className="[&_svg]:size-5"
            >
              <X />
            </Button>
          </div>
          <div className="space-y-4 max-h-full px-5 pb-5 h-0 flex-grow overflow-hidden flex flex-col">
            <Input
              placeholder="Search"
              className="pl-9"
              title="Search by Node Type, Title, or Description"
              startContent={<SearchIcon className="size-4 text-secondary" />}
              onChange={e => setSearch(e.target.value)}
            />
            <div className="space-y-2 max-h-full overflow-y-auto -mr-5 pr-4.5">
              {searchResults.map(result => {
                return (
                  <SearchResultItem
                    key={result.id}
                    nodeType={result.nodeType}
                    title={result.title}
                    path={result.path}
                    onClick={() => {
                      const node = getNode(result.id);
                      fitToNode(node as Node<FlowNodeData>);
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </Drawer>
    )
  );
};

export default Search;
